import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ArgumentOutOfRangeError, map, Observable, switchMap } from 'rxjs';
import { genericFormInterface } from 'src/app/util/interfaces/genericForm';
import { environment } from 'src/environments/environment';
import { AuthLoginService } from 'src/app/page/auth-page/service/auth-login.service';
import { ciInterface } from 'src/app/util/interfaces/ci';
import { spreadsheetInterface } from 'src/app/util/interfaces/spreadsheet';
import { ProfileService } from 'src/app/services/profile/profile.service';
import { LabelsService } from 'src/app/util/services/labels/labels.service';
import { isNgTemplate } from '@angular/compiler';

@Injectable({
	providedIn: 'root',
})
export class CmdbExportPageService {
	private getAvailableFiltersByTypeUrl = environment.baseApiUrl + 'cmdb/search/field/{queryString}';
	private getCiListUrl = environment.baseApiUrl + 'cmdb/search?{queryString}';
	private getCiTypeFormUrl = environment.baseApiUrl + 'cmdb/citype/{idCiType}?customer_id={idCustomer}';

	constructor(
		private AuthLoginService: AuthLoginService,
		private ProfileService: ProfileService,
		private http: HttpClient,
		private LabelsService: LabelsService
	) {}

	getAvailableFilters(idCiType?: string): Observable<genericFormInterface[]> {
		const headers = new HttpHeaders({
			Authorization: 'Bearer ' + this.AuthLoginService.getSessionToken(),
			'Content-Type': 'application/json;charset=UTF-8',
		});
		if (!idCiType) {
			idCiType = '0';
		}
		const getAvailableFiltersByTypeUrl = this.getAvailableFiltersByTypeUrl.replace('{queryString}', `?idCiType=${idCiType}`);
		return this.http.get<any[]>(getAvailableFiltersByTypeUrl, { headers: headers }).pipe(
			map((filtersRes) => {
				const newCmdbFilterArray: genericFormInterface[] = [];

				for (const filter of filtersRes) {
					const newCmdbFilter: genericFormInterface = {
						id: filter.name,
						name: filter.name,
						inputType: filter.inputType,
						label: filter.label,
						values: [''],
						items: filter.items,
						commonGroup: filter.ciType,
					};

					newCmdbFilterArray.push(newCmdbFilter);
				}

				return newCmdbFilterArray;
			})
		);
	}

	getExportableCMDBList(queryString: string): Observable<spreadsheetInterface> {
		const headers = new HttpHeaders({
			Authorization: 'Bearer ' + this.AuthLoginService.getSessionToken(),
			'Content-Type': 'application/json;charset=UTF-8',
		});
		const getCiListUrl = this.getCiListUrl.replace('{queryString}', queryString);
		const temporaryIdCiType = queryString.includes('&type=') ? queryString.trim().split('&type=')[1].split('&')[0] : '1';
		let idCustomer = '';
		let getCiTypeFormUrl = '';

		if (queryString.includes('&user_type=') && queryString.includes('user_language') && queryString.includes('customer_id')) {
			getCiTypeFormUrl = this.getCiTypeFormUrl
				.replace('{idCiType}', temporaryIdCiType)
				.replace('{idCustomer}', queryString.trim().split('&customer_id=')[1].split('&')[0])
				.concat('&user_type=', queryString.trim().split('&user_type=')[1]);
			idCustomer = getCiTypeFormUrl.split('customer_id=')[1].split('&')[0];
		} else {
			getCiTypeFormUrl = this.getCiTypeFormUrl
				.replace('{idCiType}', temporaryIdCiType)
				.replace('{idCustomer}', this.ProfileService.userData().idCustomer);
			idCustomer = this.ProfileService.userData().idCustomer.toString();
		}

		return this.http.get<any>(getCiTypeFormUrl, { headers: headers }).pipe(
			switchMap((cmdbFields) => {
				return this.LabelsService.getLabels().pipe(
					switchMap((labels) => {
						return this.http.get<JSON[]>(getCiListUrl, { headers: headers }).pipe(
							map((ciListRes: any) => {
								let concatCmdb = cmdbFields.cmdb;
								if (queryString.includes('&type=')) {
									concatCmdb = concatCmdb.concat(cmdbFields.ci);
								}
								if (cmdbFields.customFields) {
									concatCmdb = concatCmdb.concat(cmdbFields.customFields);
								}

								const ciKeys = ciListRes && ciListRes.length > 0 ? Object.keys(ciListRes[0]) : [];
								ciKeys.sort((a, b) => a.localeCompare(b));
								const newSpreadsheet: spreadsheetInterface = {
									id: '',
									title: '',
									selectedItemAxis: [-1, -1],
									sort: { sort_column: 0, sort_down: true },
									hiddenLines: [],
									lines: [],
								};

								const headerLine: spreadsheetInterface['lines'][number] = {
									generatedBlank: false,
									cells: [],
								};

								//Headers
								for (const column_name of ciKeys) {
									for (const cmdbField of concatCmdb) {
										if (cmdbField.name === column_name) {
											const newHeaderCell: spreadsheetInterface['lines'][number]['cells'][number] = {
												header_cell: { search_value: '' },
												item_name: labels[cmdbField.label] ? labels[cmdbField.label] : column_name,
												item_value: labels[cmdbField.label] ? labels[cmdbField.label] : column_name,
											};
											headerLine.cells.push(newHeaderCell);
										}
									}
								}
								newSpreadsheet.lines.push(headerLine);

								//Content
								for (const ciItem of ciListRes) {
									const newLine: spreadsheetInterface['lines'][number] = {
										generatedBlank: false,
										cells: [],
									};
									for (const column_name of ciKeys) {
										for (const cmdbField of concatCmdb) {
											if (cmdbField.name === column_name) {
												let cellValue = '';
												if (cmdbField.inputType === 'boolean') {
													cellValue =
														String(ciItem[column_name]) === 'true' || String(ciItem[column_name]) === '1'
															? labels.lbl_geral_sim
															: labels.lbl_geral_nao;
												}
												if (cmdbField.inputType === 'select' && cmdbField.items && cmdbField.items.length > 0) {
													for (const item of cmdbField.items) {
														if (item.value === ciItem[column_name]) {
															cellValue = labels[item.label] ? labels[item.label] : item.name;
														}
														if (
															!ciItem[column_name] ||
															ciItem[column_name] === '' ||
															item.label === '' ||
															item.name === ''
														) {
															cellValue = labels.lbl_geral_nao_definido;
														}
													}
												}
												if (cmdbField.inputType === 'date') {
													if (!ciItem[column_name] || ciItem[column_name] === '') {
														cellValue = labels.lbl_geral_nao_definido;
													} else {
														const date = new Date(ciItem[column_name]);
														cellValue = `${date.getDate() < 10 ? '0' + date.getDate() : date.getDate()}/${date.getMonth() < 10 ? '0' + date.getMonth() : date.getMonth()}/${date.getFullYear()}`;
													}
												}
												if (cmdbField.inputType === 'number') {
													if (!ciItem[column_name] || ciItem[column_name] === '0' || ciItem[column_name] === '') {
														cellValue = labels.lbl_geral_nao_definido;
													} else {
														cellValue = ciItem[column_name] ? String(ciItem[column_name]) : ciItem[column_name];
													}
													if (cmdbField.inputType === 'number' && cmdbField.name === 'type') {
														switch (ciItem.type) {
															case 1: {
																cellValue = labels.lbl_asset_impressora;
																break;
															}

															case 2: {
																cellValue = labels.lbl_cmdb_scanner;
																break;
															}

															case 3: {
																cellValue = labels.lbl_asset_micro;
																break;
															}

															case 4: {
																cellValue = labels.lbl_asset_servidor;
																break;
															}

															case 5: {
																cellValue = labels.lbl_asset_roteador;
																break;
															}

															case 6: {
																cellValue = labels.lbl_cmdb_switch;
																break;
															}

															case 7: {
																cellValue = labels.lbl_geral_ponto_rede;
																break;
															}

															case 8: {
																cellValue = labels.lbl_asset_inidade_fita;
																break;
															}

															case 9: {
																cellValue = labels.lbl_geral_alicacao;
																break;
															}

															case 10: {
																cellValue = labels.lbl_geral_banco_dados;
																break;
															}

															case 11: {
																cellValue = labels.lbl_asset_contrato_manutencao;
																break;
															}

															case 12: {
																cellValue = labels.lbl_cmdb_type_link;
																break;
															}

															case 13: {
																cellValue = labels.lbl_cmdb_rack;
																break;
															}

															case 14: {
																cellValue = labels.lbl_cmdb_storage;
																break;
															}

															case 15: {
																cellValue = labels.lbl_asset_cartao_vpn;
																break;
															}

															case 16: {
																cellValue = labels.lbl_cmdb_miscellaneous;
																break;
															}

															case 17: {
																cellValue = labels.lbl_cmdb_hub;
																break;
															}

															case 19: {
																cellValue = labels.lbl_cmdb_colocation;
																break;
															}

															case 20: {
																cellValue = labels.lbl_cmdb_licenca;
																break;
															}

															case 21: {
																cellValue = labels.lbl_asset_fita;
																break;
															}

															case 22: {
																cellValue = labels.lbl_asset_banda_internet;
																break;
															}

															case 23: {
																cellValue = labels.lbl_asset_conexao_tpg;
																break;
															}

															case 24: {
																cellValue = labels.lbl_asset_inventario_storage;
																break;
															}

															case 25: {
																cellValue = labels.lbl_cmdb_dsl;
																break;
															}

															case 27: {
																cellValue = labels.lbl_cmdb_switch_port;
																break;
															}

															case 28: {
																cellValue = labels.lbl_projetos_politica_backup;
																break;
															}

															case 29: {
																cellValue = labels.lbl_cmdb_access_point;
																break;
															}

															case 30: {
																cellValue = labels.lbl_cmdb_firewall;
																break;
															}

															case 37: {
																cellValue = labels.lbl_cmdb_telefonia;
																break;
															}

															case 38: {
																cellValue = labels.lbl_cmdb_itservices;
																break;
															}

															case 39: {
																cellValue = labels.lbl_asset_peripheral;
																break;
															}

															default: {
																cellValue = ciItem.type;
																break;
															}
														}
													}
												}
												if (
													cmdbField.inputType === 'string' &&
													(!ciItem[column_name] || ciItem[column_name] === '')
												) {
													cellValue = labels.lbl_geral_nao_definido;
												}

												if (cellValue === '') {
													cellValue = ciItem[column_name] ? String(ciItem[column_name]) : ciItem[column_name];
												}

												const newCell: spreadsheetInterface['lines'][number]['cells'][number] = {
													item_name: cellValue,
													item_value: cellValue,
												};
												newLine.cells.push(newCell);
											}
										}
									}

									newSpreadsheet.lines.push(newLine);
								}

								return newSpreadsheet;
							})
						);
					})
				);
			})
		);
	}
}
